import Grid3x3Icon from '@mui/icons-material/Grid3x3';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { useState } from 'react';

import { useActiveDesignContext } from '../context/active-design-context.js';
import { GridSettings } from '../grid-settings/grid-settings.js';
import { useCadRendererGridSync } from '../sync/cad-renderer-sync-context.js';
import { GridPosition } from './grid-position.js';
import { isValidDistance } from './grid-utils.js';

import type { ModelGrid } from '@cadstrom/sigma-engine-shared';

export type GridViewProps = {
	modelGrid: ModelGrid;
};

export function GridView({ modelGrid }: GridViewProps) {
	const { activeDesign } = useActiveDesignContext();
	const { gridSettings } = useCadRendererGridSync(activeDesign.designId, activeDesign.modelType, modelGrid);
	const [gridPopoverOpen, setGridPopoverOpen] = useState(false);
	const [gridPopoverAnchorEl, setGridPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
	const { distance, units } = gridSettings;

	return (
		<div className='flex items-center gap-2'>
			<div className='flex items-center gap-0.5'>
				<Tooltip placement='top-start' title={<p className='text-sm'>Configure viewing grid</p>} disableInteractive>
					<IconButton ref={(node) => setGridPopoverAnchorEl(node)} onClick={() => setGridPopoverOpen(true)}>
						<Grid3x3Icon />
					</IconButton>
				</Tooltip>

				<p className='text-sm whitespace-nowrap text-gray-a10'>
					{!isValidDistance(distance, units) ? '??' : parseFloat(distance.toPrecision(8))} {units}
				</p>
			</div>

			<GridPosition
				grid={{
					...modelGrid,
					distance,
					units,
				}}
			/>

			<Popover
				open={gridPopoverOpen}
				anchorEl={gridPopoverAnchorEl}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				classes={{
					paper: clsx('flex flex-col gap-4 p-3'),
				}}
				onClose={() => setGridPopoverOpen(false)}
			>
				<h4 className='text-xl font-semibold'>Grid settings</h4>

				<GridSettings modelGrid={modelGrid} />
			</Popover>
		</div>
	);
}
